import { useEffect, useMemo, useState } from 'react'
import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { DateRangePicker, FilterBar, SearchField, type FilterOption } from '@/components/pickers'
import { List, ListGroupLabel, TransactionRow } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, NoResults, SkeletonList } from '@/components/states'
import type { Transaction } from '@/generated/types'
import { api, type TransactionQuery } from '@/lib/api'
import { t } from '@/lib/copy'
import { formatMonth, monthKeyOf } from '@/lib/format'
import { Link, useNavigate, useRoute } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

type Filter = 'all' | 'spending' | 'income' | 'recurring' | 'fees' | 'uncategorized'

const FILTERS: FilterOption<Filter>[] = [
  { value: 'all', labelKey: 'transactions.filter.all' },
  { value: 'spending', labelKey: 'transactions.filter.spending' },
  { value: 'income', labelKey: 'transactions.filter.income' },
  { value: 'recurring', labelKey: 'transactions.filter.recurring' },
  { value: 'fees', labelKey: 'transactions.filter.fees' },
  { value: 'uncategorized', labelKey: 'transactions.filter.uncategorized' },
]

const FILTER_VALUES = new Set<string>(FILTERS.map((f) => f.value))

function queryFor(filter: Filter): Partial<TransactionQuery> {
  switch (filter) {
    case 'spending':
      return { direction: 'outflow' }
    case 'income':
      return { direction: 'inflow' }
    case 'recurring':
      return { recurring: true }
    case 'fees':
      return { fees: true }
    case 'uncategorized':
      return { uncategorized: true }
    default:
      return {}
  }
}

/** The full ledger: search, filter chips, date range, cursor pagination. */
export function TransactionsScreen() {
  const navigate = useNavigate()
  const route = useRoute()

  const requested = route.query.get('filter') as Filter | null
  const [filter, setFilter] = useState<Filter>(
    requested && FILTER_VALUES.has(requested) ? requested : 'all',
  )
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [range, setRange] = useState({ from: '', to: '' })
  const [extra, setExtra] = useState<Transaction[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  const accountId = route.query.get('accountId') ?? undefined
  const category = (route.query.get('category') as TransactionQuery['category']) ?? undefined

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const baseQuery = useMemo<TransactionQuery>(
    () => ({
      ...queryFor(filter),
      q: debounced || undefined,
      from: range.from || undefined,
      to: range.to || undefined,
      accountId,
      category,
      limit: 50,
    }),
    [filter, debounced, range.from, range.to, accountId, category],
  )

  const page = useAsync((signal) => api.transactions.list(baseQuery, signal), [baseQuery])

  useEffect(() => {
    setExtra([])
    setCursor(page.state.data?.nextCursor ?? null)
  }, [page.state.data])

  const items = [...(page.state.data?.items ?? []), ...extra]
  const filtersActive = filter !== 'all' || debounced !== '' || range.from !== '' || range.to !== ''

  const screenState = loadingMore
    ? 'loadingMore'
    : page.state.status === 'error'
      ? 'error'
      : page.state.status === 'loading' && !page.state.data
        ? 'loading'
        : items.length === 0
          ? filtersActive
            ? 'noResults'
            : 'empty'
          : 'ready'

  const clearFilters = () => {
    setFilter('all')
    setSearch('')
    setDebounced('')
    setRange({ from: '', to: '' })
  }

  const loadMore = async () => {
    if (!cursor) return
    setLoadingMore(true)
    try {
      const next = await api.transactions.list({ ...baseQuery, cursor })
      setExtra((rows) => [...rows, ...next.items])
      setCursor(next.nextCursor ?? null)
    } finally {
      setLoadingMore(false)
    }
  }

  const grouped = useMemo(() => {
    const groups: { month: string; rows: Transaction[] }[] = []
    for (const tx of items) {
      const month = monthKeyOf(tx.date)
      const last = groups[groups.length - 1]
      if (last && last.month === month) last.rows.push(tx)
      else groups.push({ month, rows: [tx] })
    }
    return groups
  }, [items])

  return (
    <Screen
      id="transactions"
      state={screenState}
      actions={
        <>
          <Link screen="accounts" className="btn btn-secondary">
            {t('accounts.title')}
          </Link>
          <Link screen="categories" className="btn btn-secondary">
            {t('categories.title')}
          </Link>
        </>
      }
    >
      <div className="stack">
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <SearchField value={search} onChange={setSearch} placeholder={t('transactions.search.placeholder')} />
          <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
        </div>

        <div className="row" style={{ flexWrap: 'wrap' }}>
          <FilterBar options={FILTERS} value={filter} onChange={setFilter} label={t('transactions.title')} />
          {filtersActive ? (
            <Button variant="quiet" onClick={clearFilters}>
              {t('transactions.filter.clear')}
            </Button>
          ) : null}
        </div>

        {screenState === 'loading' ? (
          <LoadingRegion>
            <SkeletonList rows={10} />
          </LoadingRegion>
        ) : screenState === 'error' ? (
          <ErrorState
            titleKey="transactions.error.title"
            error={page.state.status === 'error' ? page.state.error : undefined}
            onRetry={page.reload}
          />
        ) : screenState === 'empty' ? (
          <EmptyState
            titleKey="transactions.empty.title"
            actionLabel={t('transactions.empty.cta')}
            onAction={() => navigate('upload')}
          />
        ) : screenState === 'noResults' ? (
          <NoResults
            titleKey="transactions.noResults.title"
            actionLabel={t('transactions.noResults.cta')}
            onAction={clearFilters}
          />
        ) : (
          <>
            <List>
              {grouped.map((group) => (
                <ListGroupLabelledRows key={group.month} month={group.month} rows={group.rows} onSelect={(id) => navigate('transactionDetail', { id })} />
              ))}
            </List>
            {cursor ? (
              <Button variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? t('state.loading') : t('transactions.cta.loadMore')}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </Screen>
  )
}

function ListGroupLabelledRows({
  month,
  rows,
  onSelect,
}: {
  month: string
  rows: Transaction[]
  onSelect: (id: string) => void
}) {
  return (
    <>
      <ListGroupLabel>{formatMonth(month)}</ListGroupLabel>
      {rows.map((tx) => (
        <TransactionRow key={tx.id} transaction={tx} onSelect={() => onSelect(tx.id)} />
      ))}
    </>
  )
}
